import React from 'react';
import {
  WebhookSettings,
  WebhookTest,
} from '@client/components/WebhookSettings';
import { CreatorCrmLayout } from './CreatorCrm.layout';
import auth from '@client/components/auth';
import { Tooltip, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { useInfluencer } from '@client/new/hooks/use-influencer';
import { useInfluencerId } from '@client/new/contexts/InfluencerIdContext';

export function CrmWebhooksPage({ params }) {
  const influencerId = params.influencerId
    ? parseInt(params.influencerId)
    : auth.getUser()?.influencerId;

  return (
    <CreatorCrmLayout influencerId={influencerId}>
      <div className="flex flex-col gap-5">
        <div className="bg-white p-10 rounded-xl">
          <div className="pb-5 flex flex-col gap-5 md:grid grid-cols-2 md:gap-20 ">
            <WebhooksExplanation />
            <TypesOfTriggers />
          </div>
          <WebhookSettings influencerId={influencerId} />
        </div>
        <div className="bg-white p-10 rounded-xl">
          <WebhookTest influencerId={influencerId} />
        </div>
      </div>
    </CreatorCrmLayout>
  );
}

function WebhooksExplanation() {
  return (
    <div>
      <Typography variant="h5" className="mb-2.5">
        Unlock the Power of Webhooks for Your Marketing Campaigns!
      </Typography>
      <Typography variant="body1" className="text-[#2C3548]">
        Set Triggers 🚀 Connect with Your Email Platform 💌 Send Targeted
        Campaigns 📈 With Webhooks, you can set up triggers for any of the
        available events, such as a New Trial or New Lead, and seamlessly
        connect them to your email platform. It's like having your own alert
        system. Use this feature to deliver messages at the right time to boost
        engagement and increase conversion rates. Get data updates, automate
        your responses, and deliver highly targeted campaigns to elevate your
        marketing game! During setup, simply input the target URL, which is
        typically found in your email platform's settings. Before making it
        live, run a quick test to ensure your Webhook is working as expected.
      </Typography>
    </div>
  );
}

function TypesOfTriggers() {
  const influencerId = useInfluencerId();
  const { influencer } = useInfluencer({ creatorId: influencerId });

  const TRIGGERS = [
    { name: 'New Lead', tooltip: TOOLTIP_NEW_LEAD },
    { name: 'New Trial', tooltip: TOOLTIP_NEW_TRIAL },
    { name: 'New Subscription', tooltip: TOOLTIP_NEW_SUBSCRIPTION },
    { name: 'New One-Time Purchase', tooltip: TOOLTIP_NEW_OTP_PURCHASE },
    {
      name: 'New Free Offer Redemption',
      tooltip: TOOLTIP_NEW_FREE_OFFER_REDEMPTION,
    },
    { name: 'Abandoned Cart', tooltip: TOOLTIP_ABANDONED_CART },
    {
      name: 'Subscription Upgrade',
      tooltip: TOOLTIP_SUBSCRIPTION_UPGRADE(influencer),
    },
    { name: 'Subscription Cancel', tooltip: TOOLTIP_SUBSCRIPTION_CANCEL },
    { name: 'Subscription Expired', tooltip: TOOLTIP_SUBSCRIPTION_EXPIRED },
    {
      name: 'Subscription Reactivated',
      tooltip: TOOLTIP_SUBSCRIPTION_REACTIVATED,
    },
  ];

  return (
    <div>
      <Typography variant="h5" className="mb-2.5">
        Types of Triggers at Your Service
      </Typography>
      <Typography variant="body1" className="text-[#2C3548]">
        <ul className="flex flex-col gap-2.5">
          {TRIGGERS.map((t) => (
            <li key={t.name}>
              {t.name}{' '}
              <Tooltip
                title={
                  <p
                    className="whitespace-pre-wrap"
                    dangerouslySetInnerHTML={{
                      __html: t.tooltip,
                    }}
                  />
                }
              >
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  className="text-[#8A8E99] hover:text-black"
                />
              </Tooltip>
            </li>
          ))}
        </ul>
      </Typography>
    </div>
  );
}

const TOOLTIP_NEW_LEAD = `
A new lead for your business is now available in your CRM's 'Leads Table.' This user may have submitted their email through your landing page or another source.
`.trim();

const TOOLTIP_NEW_TRIAL = `
A user has just initiated a free trial, which can be converted to either a monthly or an annual plan.
`.trim();

const TOOLTIP_NEW_SUBSCRIPTION = `
A user has just purchased a new paid subscription or has completed their trial and started their paid subscription.`.trim();

const TOOLTIP_NEW_OTP_PURCHASE = `
A user has just made a purchase of one of your paid programs.
`;

const TOOLTIP_NEW_FREE_OFFER_REDEMPTION = `
A user has just redeemed one of your free offers.
`;
const TOOLTIP_ABANDONED_CART = `
A user started checkout for one of your products but did not complete the purchase. They receive a webhook about an hour after abandoning, so you can follow up and win the sale back.
`.trim();

const TOOLTIP_SUBSCRIPTION_UPGRADE = (influencer?: any) => {
  if (influencer?.visibility === 'limited') {
    return `
    A user upgraded their subscription to an annual plan, or if they were on a limited plan, they upgraded to a premium plan.
    `.trim();
  }

  return 'A user upgraded their subscription to an annual plan.';
};

const TOOLTIP_SUBSCRIPTION_CANCEL = `
A user has just initiated the cancellation of their subscription. They may retain access to the app until the expiration date.

<b>Tip:</b> You can schedule marketing campaigns to target these users and encourage them to resubscribe.
`.trim();

const TOOLTIP_SUBSCRIPTION_EXPIRED = `
A user has canceled their subscription and is now inactive. They no longer have access to the app as the expiration date has already passed.
`.trim();

const TOOLTIP_SUBSCRIPTION_REACTIVATED = `
A user who had previously canceled their subscription and had turned off 'auto-renew' has now re-subscribed and reactivated it. You can utilize this as a conversion event in your email platform. Note: If their subscription had already expired and they resubscribed, they will appear as a "New Subscriber."
`.trim();
